import RentalStatus from "../enums/RentalStatus";
import APIUrl from "../APIUrl";

/**
 * Filtre la liste des DM selon les critères de recherche et exclut les DM déjà en location
 */
export const getFilteredDmList = (
  dmList,
  rentals,
  establishmentFilter,
  dmSearchTerm,
) => {
  // S'assurer que rentals est un tableau
  const rentalsList = Array.isArray(rentals) ? rentals : [];

  // Récupérer les références internes des DM déjà en location active
  const activeDmReferences = rentalsList
    .filter(
      (rental) =>
        rental &&
        rental.is_dm_rental &&
        (rental.status === RentalStatus.ACTIF ||
          rental.status === RentalStatus.EN_ATTENTE),
    )
    .map((rental) => rental.product?.ref?.toString())
    .filter((ref) => ref);

  // S'assurer que dmList est un tableau avant de filtrer
  if (!Array.isArray(dmList)) {
    return [];
  }

  // Filtrer les DM par établissement ET exclure ceux déjà en location
  let filteredDmList = dmList.filter((dm) => {
    // Exclure les DM déjà en location (comparer par référence interne)
    if (activeDmReferences.includes(dm.reference?.toString())) {
      return false;
    }

    // Filtrer par établissement si spécifié
    if (establishmentFilter) {
      const matchesEstablishment =
        dm.establishment &&
        dm.establishment
          .toLowerCase()
          .includes(establishmentFilter.toLowerCase());
      if (!matchesEstablishment) return false;
    }

    // Filtrer par terme de recherche (ref, brand, designation, model)
    if (dmSearchTerm) {
      const searchLower = dmSearchTerm.toLowerCase();
      const matchesSearch =
        (dm.reference && dm.reference.toLowerCase().includes(searchLower)) ||
        (dm.brand && dm.brand.toLowerCase().includes(searchLower)) ||
        (dm.designation &&
          dm.designation.toLowerCase().includes(searchLower)) ||
        (dm.model && dm.model.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;
    }

    return true;
  });

  return filteredDmList;
};

/**
 * Retourne la liste des établissements uniques à partir de la liste des DM
 */
export const getUniqueEstablishments = (dmList) => {
  const establishments = new Set();
  dmList.forEach((dm) => {
    if (dm.establishment && dm.establishment !== "N/A") {
      establishments.add(dm.establishment);
    }
  });
  return Array.from(establishments).sort();
};

/**
 * Valide les données de location des DM sélectionnés
 */
export const validateDmRentalData = (selectedDmIds, dmRentalData) => {
  for (const dmId of selectedDmIds) {
    const data = dmRentalData[dmId];

    // Vérifier les champs obligatoires
    if (!data || !data.startDate || !data.dailyPrice) {
      return false;
    }

    // Vérifier que la date de fin n'est pas antérieure à la date de début
    if (data.endDate && data.startDate && data.endDate < data.startDate) {
      return false;
    }
  }

  return true;
};

/**
 * Prépare les données pour l'API de création de locations DM
 */
export const prepareDmRentalsForApi = (selectedDmIds, dmList, dmRentalData) => {
  return selectedDmIds.map((dmId) => {
    const dm = dmList.find((d) => d._id === dmId);
    const rentalData = dmRentalData[dmId];

    // Vérifier si l'équipement n'avait pas de date de début de location
    const shouldUpdateEquipmentRentalStartDate = !dm.rentalStartDate;

    return {
      dmId: dmId,
      equipmentData: {
        _id: dm._id,
        reference: dm.reference,
        designation: dm.designation,
        brand: dm.brand,
        model: dm.model,
        establishment: dm.establishment,
        purchaseDate: dm.purchaseDate,
      },
      startDate: rentalData.startDate,
      endDate: rentalData.endDate,
      dailyPrice: parseFloat(rentalData.dailyPrice),
      shouldUpdateEquipmentRentalStartDate: shouldUpdateEquipmentRentalStartDate,
    };
  });
};

/**
 * Récupère la liste des DM depuis l'API
 */
export const fetchDmList = async () => {
  const endpoint = `${APIUrl.rootUrl}/dm/psdm-list`;
  const token = APIUrl.jwtToken || sessionStorage.getItem("jwtToken");

  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      jwtToken: token,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.dm || [];
};
